import React, { useState, useEffect, useCallback, useRef } from "react";
import { getTokenInternal } from "./useToken";
import Nav from "./Navigation";
import { useNavigate } from "react-router-dom";

const ManageImages = () => {
  const [token, setToken] = useState(null);
  const [images, setImages] = useState([]);
  const [privateImages, setPrivateImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [customName, setCustomName] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [copiedFilename, setCopiedFilename] = useState(null);
  const [deleteConfirmFilename, setDeleteConfirmFilename] = useState(null);
  const [deleteConfirmPrivate, setDeleteConfirmPrivate] = useState(null);
  const [destination, setDestination] = useState("public");
  const [publishingFilename, setPublishingFilename] = useState(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const fetchImages = useCallback(async (tok) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_HOST}/api/images`, {
        headers: { Authorization: `Bearer ${tok.access_token}` },
      });
      if (res.ok) setImages(await res.json());
    } catch (e) {
      console.error("Error fetching images:", e);
    }
  }, []);

  const fetchPrivateImages = useCallback(async (tok) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_HOST}/api/images/private`, {
        headers: { Authorization: `Bearer ${tok.access_token}` },
      });
      if (res.ok) setPrivateImages(await res.json());
    } catch (e) {
      console.error("Error fetching private images:", e);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      const tok = await getTokenInternal();
      if (!tok) return;
      setToken(tok);
      if (!["owner", "management", "helper"].includes(tok.account.role)) {
        navigate("/");
        return;
      }
      fetchImages(tok);
      if (["owner", "management"].includes(tok.account.role)) {
        fetchPrivateImages(tok);
      }
    };
    init();
  }, [navigate, fetchImages, fetchPrivateImages]);

  const handleFileChange = (file) => {
    if (!file) return;
    setSelectedFile(file);
    setUploadResult(null);
    setUploadError(null);
    if (!customName) setCustomName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileChange(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile) return;

    setUploading(true);
    setUploadResult(null);
    setUploadError(null);

    try {
      const form = new FormData();
      form.append("file", selectedFile);
      if (customName.trim()) form.append("custom_name", customName.trim());

      const endpoint = destination === "private"
        ? "/api/images/upload-private"
        : "/api/images/upload";

      const res = await fetch(`${process.env.REACT_APP_API_HOST}${endpoint}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token.access_token}` },
        body: form,
      });

      if (res.ok) {
        const data = await res.json();
        setUploadResult({ ...data, destination });
        setSelectedFile(null);
        setPreview(null);
        setCustomName("");
        if (destination === "private") {
          fetchPrivateImages(token);
        } else {
          fetchImages(token);
        }
      } else {
        const err = await res.json();
        setUploadError(err.detail || "Upload failed");
      }
    } catch (e) {
      setUploadError("Upload failed — check your connection");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (filename) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_HOST}/api/images/${encodeURIComponent(filename)}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token.access_token}` },
        }
      );
      if (res.ok) {
        setImages((prev) => prev.filter((img) => img.filename !== filename));
        setDeleteConfirmFilename(null);
      }
    } catch (e) {
      console.error("Delete failed:", e);
    }
  };

  const handleDeletePrivate = async (filename) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_HOST}/api/images/private/${encodeURIComponent(filename)}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token.access_token}` },
        }
      );
      if (res.ok) {
        setPrivateImages((prev) => prev.filter((img) => img.filename !== filename));
        setDeleteConfirmPrivate(null);
      }
    } catch (e) {
      console.error("Delete failed:", e);
    }
  };

  const handlePublish = async (filename) => {
    setPublishingFilename(filename);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_HOST}/api/images/publish/${encodeURIComponent(filename)}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token.access_token}` },
        }
      );
      if (res.ok) {
        setPrivateImages((prev) => prev.filter((img) => img.filename !== filename));
        fetchImages(token);
      }
    } catch (e) {
      console.error("Publish failed:", e);
    } finally {
      setPublishingFilename(null);
    }
  };

  const copyFilename = (filename) => {
    navigator.clipboard.writeText(filename);
    setCopiedFilename(filename);
    setTimeout(() => setCopiedFilename(null), 2000);
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (iso) => new Date(iso).toLocaleString();

  const isManagement = ["owner", "management"].includes(token?.account?.role);

  return (
    <div>
      <Nav />
      <div className="p-4 max-w-5xl mx-auto">
        <h1 className="text-white text-2xl font-bold mb-6">Manage Images</h1>

        {/* Upload form */}
        <div className="bg-gray-800 rounded-lg p-4 mb-8">
          <h2 className="text-white text-lg font-semibold mb-3">Upload Image</h2>

          {/* Destination toggle */}
          {isManagement && (
            <div className="flex gap-2 mb-4">
              <button
                type="button"
                onClick={() => setDestination("public")}
                className={`px-4 py-2 rounded-md text-sm font-semibold border-2 transition-colors ${
                  destination === "public"
                    ? "bg-green-700 border-green-500 text-white"
                    : "bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600"
                }`}
              >
                Live (Public)
              </button>
              <button
                type="button"
                onClick={() => setDestination("private")}
                className={`px-4 py-2 rounded-md text-sm font-semibold border-2 transition-colors ${
                  destination === "private"
                    ? "bg-yellow-700 border-yellow-500 text-white"
                    : "bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600"
                }`}
              >
                Pre-launch (Private)
              </button>
            </div>
          )}

          {destination === "private" && (
            <p className="text-yellow-300 text-xs mb-3 bg-yellow-900 bg-opacity-30 border border-yellow-700 rounded px-3 py-2">
              This image will be stored privately and will NOT be visible to users. Publish it from the Pre-launch section below when you're ready to go live.
            </p>
          )}

          <form onSubmit={handleSubmit}>
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer mb-4 transition-colors ${
                dragging ? "border-blue-400 bg-blue-900 bg-opacity-20" : "border-gray-500 hover:border-gray-400"
              }`}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              {preview ? (
                <img src={preview} alt="Preview" className="max-h-40 mx-auto rounded" />
              ) : (
                <p className="text-gray-400">
                  Drag & drop an image here, or click to browse
                </p>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleFileChange(e.target.files[0])}
              />
            </div>

            {selectedFile && (
              <div className="mb-4">
                <label className="block text-gray-300 text-sm mb-1">
                  Filename (edit to rename before uploading):
                </label>
                <input
                  type="text"
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  className="p-2 rounded text-black w-full max-w-md text-sm"
                  placeholder="filename.png"
                />
                <p className="text-gray-500 text-xs mt-1">
                  This is the name you'll type into the item URL field.
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={!selectedFile || uploading}
              className={`disabled:opacity-40 text-white px-5 py-2 rounded-md text-sm font-semibold ${
                destination === "private"
                  ? "bg-yellow-700 hover:bg-yellow-600"
                  : "bg-green-600 hover:bg-green-700"
              }`}
            >
              {uploading
                ? "Uploading…"
                : destination === "private"
                ? "Upload to Pre-launch"
                : "Upload"}
            </button>
          </form>

          {uploadResult && (
            <div className={`mt-4 rounded p-3 border ${
              uploadResult.destination === "private"
                ? "bg-yellow-900 bg-opacity-40 border-yellow-600"
                : "bg-green-900 bg-opacity-40 border-green-600"
            }`}>
              <p className={`font-semibold text-sm mb-1 ${uploadResult.destination === "private" ? "text-yellow-300" : "text-green-300"}`}>
                {uploadResult.destination === "private"
                  ? "Uploaded to Pre-launch (private) — not yet visible to users."
                  : "Upload successful!"}
              </p>
              <p className="text-gray-300 text-sm">
                Filename:{" "}
                <span className="font-mono font-bold text-white">{uploadResult.filename}</span>
                <button
                  onClick={() => copyFilename(uploadResult.filename)}
                  className="ml-2 text-xs bg-gray-600 hover:bg-gray-500 text-white px-2 py-0.5 rounded"
                >
                  {copiedFilename === uploadResult.filename ? "Copied!" : "Copy"}
                </button>
              </p>
            </div>
          )}

          {uploadError && (
            <p className="mt-3 text-red-400 text-sm">{uploadError}</p>
          )}
        </div>

        {/* Pre-launch images */}
        {isManagement && (
          <div className="mb-10">
            <h2 className="text-white text-lg font-semibold mb-1">
              Pre-launch Images{" "}
              <span className="text-yellow-400 text-sm font-normal">({privateImages.length})</span>
              <span className="ml-2 text-xs bg-yellow-800 text-yellow-200 px-2 py-0.5 rounded">Private — not visible to users</span>
            </h2>
            <p className="text-gray-400 text-xs mb-3">
              Click Publish to move an image to the live public bucket. The item will show immediately once the image is published and the item is activated.
            </p>

            {privateImages.length === 0 ? (
              <p className="text-gray-500 text-sm">No pre-launch images staged.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {privateImages.map((img) => (
                  <div key={img.filename} className="bg-gray-800 border border-yellow-800 rounded-lg overflow-hidden">
                    <div className="w-full h-28 bg-gray-900 flex items-center justify-center">
                      <span className="text-gray-500 text-xs text-center px-2">🔒 {img.filename}</span>
                    </div>
                    <div className="p-2">
                      <p className="text-white text-xs font-mono truncate" title={img.filename}>
                        {img.filename}
                      </p>
                      <p className="text-gray-500 text-xs">{formatSize(img.size)}</p>
                      <p className="text-gray-500 text-xs">{formatDate(img.uploaded_at)}</p>
                      <div className="flex gap-1 mt-2">
                        <button
                          onClick={() => handlePublish(img.filename)}
                          disabled={publishingFilename === img.filename}
                          className="flex-1 text-xs bg-green-700 hover:bg-green-600 disabled:opacity-50 text-white py-1 rounded font-semibold"
                        >
                          {publishingFilename === img.filename ? "Publishing…" : "Publish"}
                        </button>
                        {deleteConfirmPrivate === img.filename ? (
                          <div className="flex gap-1">
                            <button
                              onClick={() => handleDeletePrivate(img.filename)}
                              className="text-xs bg-red-700 hover:bg-red-800 text-white px-2 py-1 rounded"
                            >
                              Yes
                            </button>
                            <button
                              onClick={() => setDeleteConfirmPrivate(null)}
                              className="text-xs bg-gray-500 hover:bg-gray-600 text-white px-2 py-1 rounded"
                            >
                              No
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setDeleteConfirmPrivate(img.filename)}
                            className="text-xs bg-red-900 hover:bg-red-800 text-white px-2 py-1 rounded"
                          >
                            Del
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Live images */}
        <div>
          <h2 className="text-white text-lg font-semibold mb-3">
            Live Images{" "}
            <span className="text-gray-400 text-sm font-normal">({images.length})</span>
          </h2>

          {images.length === 0 ? (
            <p className="text-gray-400 text-sm">No images uploaded yet.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {images.map((img) => (
                <div key={img.filename} className="bg-gray-800 rounded-lg overflow-hidden">
                  <img
                    src={img.url}
                    alt={img.filename}
                    className="w-full h-28 object-contain bg-gray-900 p-1"
                    onError={(e) => { e.target.src = "/gamelogos/Unofficial.PNG"; }}
                  />
                  <div className="p-2">
                    <p className="text-white text-xs font-mono truncate" title={img.filename}>
                      {img.filename}
                    </p>
                    <p className="text-gray-500 text-xs">{formatSize(img.size)}</p>
                    <p className="text-gray-500 text-xs">{formatDate(img.uploaded_at)}</p>
                    <div className="flex gap-1 mt-2">
                      <button
                        onClick={() => copyFilename(img.filename)}
                        className="flex-1 text-xs bg-gray-600 hover:bg-gray-500 text-white py-1 rounded"
                      >
                        {copiedFilename === img.filename ? "Copied!" : "Copy name"}
                      </button>
                      {isManagement && (
                        deleteConfirmFilename === img.filename ? (
                          <div className="flex gap-1">
                            <button
                              onClick={() => handleDelete(img.filename)}
                              className="text-xs bg-red-700 hover:bg-red-800 text-white px-2 py-1 rounded"
                            >
                              Yes
                            </button>
                            <button
                              onClick={() => setDeleteConfirmFilename(null)}
                              className="text-xs bg-gray-500 hover:bg-gray-600 text-white px-2 py-1 rounded"
                            >
                              No
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setDeleteConfirmFilename(img.filename)}
                            className="text-xs bg-red-800 hover:bg-red-700 text-white px-2 py-1 rounded"
                          >
                            Del
                          </button>
                        )
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageImages;
